import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Heart, Shield, Utensils, Home, Activity, Users, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import DonationWidget from "../components/DonationWidget";

const HERO_IMG = "https://images.unsplash.com/photo-1758686254593-7c4cd55b2621?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxlbGRlcmx5JTIwc21pbGluZyUyMHdhcm18ZW58MHx8fHwxNzc2NzAyOTMxfDA&ixlib=rb-4.1.0&q=85";
const IMPACT_IMG = "https://images.pexels.com/photos/36706841/pexels-photo-36706841.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const GALLERY_1 = "https://images.unsplash.com/photo-1613394242132-1268854bde44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxlbGRlcmx5JTIwc21pbGluZyUyMHdhcm18ZW58MHx8fHwxNzc2NzAyOTMxfDA&ixlib=rb-4.1.0&q=85";
const GALLERY_2 = "https://images.unsplash.com/photo-1773234005378-ceebf3c877b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxoYW5kcyUyMGhvbGRpbmclMjBlbGRlcmx5JTIwY2FyZXxlbnwwfHx8fDE3NzY3MDI5MzF8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const [config, setConfig] = useState({ charity_name: "Golden Years Care", monthly_goal_usd: 10000, min_donation_usd: 0.5 });
  const [stats, setStats] = useState({ monthly_raised: 0, monthly_goal: 10000, progress_percent: 0, donor_count: 0 });

  useEffect(() => {
    api.get("/config").then(r => setConfig(r.data)).catch(() => {});
    api.get("/donations/stats").then(r => setStats(r.data)).catch(() => {});
  }, []);

  const scrollToDonate = () => {
    document.getElementById("donate")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-alabaster text-ink-body" data-testid="landing-page">
      {/* ===== Header ===== */}
      <header className="sticky top-0 z-40 bg-alabaster/80 backdrop-blur-md border-b border-stone-border/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="site-logo">
            <Heart className="w-6 h-6 text-terracotta fill-terracotta" strokeWidth={1.5} />
            <span className="font-heading text-2xl font-semibold text-ink">{config.charity_name}</span>
          </div>
          <nav className="hidden md:flex items-center gap-10 text-sm font-medium text-ink-body">
            <a href="#mission" className="hover:text-terracotta transition-colors" data-testid="nav-mission">Our Mission</a>
            <a href="#impact" className="hover:text-terracotta transition-colors" data-testid="nav-impact">Impact</a>
            <a href="#transparency" className="hover:text-terracotta transition-colors" data-testid="nav-transparency">Transparency</a>
          </nav>
          <button
            onClick={scrollToDonate}
            data-testid="header-donate-btn"
            className="bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300 shadow-soft hover:shadow-medium"
          >
            Donate Now
          </button>
        </div>
      </header>

      {/* ===== Hero ===== */}
      <section className="relative overflow-hidden" id="mission">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-32 grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative z-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-terracotta-light border border-terracotta/20 mb-8">
              <Sparkles className="w-3.5 h-3.5 text-terracotta" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta">A home. A family. A future.</span>
            </div>

            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-ink font-semibold">
              Bring warmth to
              <br />
              <em className="text-terracotta not-italic font-normal italic">their twilight years.</em>
            </h1>

            <p className="mt-8 text-lg md:text-xl leading-relaxed text-ink-body max-w-xl">
              Your donation provides shelter, meals, medical care, and companionship for abandoned elderly who have no one else. Every dollar counts — starting from just <span className="font-semibold text-ink">$0.50</span>.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={scrollToDonate}
                data-testid="hero-donate-btn"
                className="group bg-terracotta hover:bg-terracotta-hover text-white rounded-full px-8 py-4 font-medium text-lg transition-all duration-300 shadow-soft hover:shadow-medium inline-flex items-center gap-2"
              >
                Donate Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <a
                href="#impact"
                data-testid="hero-learn-btn"
                className="border border-stone-border hover:border-terracotta text-ink-body hover:text-terracotta rounded-full px-8 py-4 font-medium text-lg transition-all duration-300 inline-flex items-center"
              >
                See our impact
              </a>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-ink-muted">
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-olive" /> Secure payments via NOWPayments</div>
              <div className="flex items-center gap-2"><Heart className="w-4 h-4 text-terracotta" /> 100% to the cause</div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-olive-light blur-3xl opacity-70" />
            <div className="absolute -bottom-10 -left-6 w-48 h-48 rounded-full bg-terracotta-light blur-3xl opacity-70" />
            <div className="relative rounded-3xl overflow-hidden shadow-medium border border-stone-border/60">
              <img src={HERO_IMG} alt="Elderly woman smiling warmly" className="w-full h-[480px] md:h-[560px] object-cover" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-2xl p-5 shadow-soft">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-olive-light flex items-center justify-center">
                    <Users className="w-5 h-5 text-olive" />
                  </div>
                  <div>
                    <div className="font-heading text-xl text-ink font-semibold">120+ elders</div>
                    <div className="text-sm text-ink-muted">currently under our care</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== Progress Tracker ===== */}
      <section className="bg-olive text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-olive-light/80 mb-2">This Month's Goal</div>
              <h3 className="font-heading text-3xl md:text-4xl font-medium">
                <span data-testid="progress-raised">${stats.monthly_raised.toLocaleString()}</span>
                <span className="text-olive-light/70"> raised of ${Number(stats.monthly_goal || config.monthly_goal_usd).toLocaleString()}</span>
              </h3>
            </div>
            <div className="text-right">
              <div className="font-heading text-5xl font-semibold" data-testid="progress-percent">{stats.progress_percent.toFixed(1)}%</div>
              <div className="text-sm text-olive-light/80">{stats.donor_count} generous donors</div>
            </div>
          </div>
          <div className="h-3 rounded-full bg-olive-hover/50 overflow-hidden" data-testid="progress-bar">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(stats.progress_percent, 100)}%` }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="h-full bg-white rounded-full"
            />
          </div>
        </div>
      </section>

      {/* ===== Donation Widget ===== */}
      <section id="donate" className="py-20 md:py-32 bg-alabaster">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="inline-block text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-4">Make a difference today</div>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-ink font-medium">
              Choose the gift of care
            </h2>
            <p className="mt-4 text-lg text-ink-body max-w-2xl mx-auto">
              Pay with card or cryptocurrency. Powered by NOWPayments — secure, instant, with automatic settlement to our wallet.
            </p>
          </div>

          <DonationWidget minAmount={config.min_donation_usd || 0.5} onSuccess={() => api.get("/donations/stats").then(r => setStats(r.data))} />
        </div>
      </section>

      {/* ===== Impact Bento Grid ===== */}
      <section id="impact" className="py-20 md:py-32 bg-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-4">Where your kindness goes</div>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-ink font-medium leading-tight">
              Every dollar becomes a meal, a medicine, a moment of dignity.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Utensils, title: "Warm Meals", desc: "Three nutritious meals every day for every resident.", stat: "$3", stat_desc: "feeds one elder / day" },
              { icon: Activity, title: "Medical Care", desc: "Regular checkups, medications, and emergency response.", stat: "$25", stat_desc: "covers weekly meds" },
              { icon: Home, title: "Safe Shelter", desc: "Clean rooms, warm bedding, and 24/7 caretaker supervision.", stat: "$50", stat_desc: "houses one elder / month" },
              { icon: Heart, title: "Companionship", desc: "Music, games, and someone to share tea with every day.", stat: "Priceless", stat_desc: "dignity & love" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`impact-card-${i}`}
                className="group bg-white rounded-3xl p-8 border border-stone-border/60 hover:-translate-y-1 transition-all duration-300 hover:shadow-medium"
              >
                <div className="w-12 h-12 rounded-2xl bg-terracotta-light flex items-center justify-center mb-6 group-hover:bg-terracotta group-hover:text-white transition-colors">
                  <item.icon className="w-5 h-5 text-terracotta group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-2xl text-ink font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-ink-body leading-relaxed mb-6">{item.desc}</p>
                <div className="pt-4 border-t border-stone-border/60">
                  <div className="font-heading text-2xl font-semibold text-terracotta">{item.stat}</div>
                  <div className="text-xs text-ink-muted mt-1">{item.stat_desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Stories / Gallery ===== */}
      <section className="py-20 md:py-32 bg-alabaster">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-4">Real stories, real lives</div>
              <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-ink font-medium leading-tight mb-6">
                Meet the people you're helping.
              </h2>
              <p className="text-lg text-ink-body leading-relaxed mb-4">
                They've lived through wars, built families, raised children. Today, many have been forgotten. But behind every wrinkled smile is a lifetime of stories — and we are their family now.
              </p>
              <p className="text-lg text-ink-body leading-relaxed">
                Your donation isn't just money. It's the knowledge that someone cared.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <motion.img
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                src={GALLERY_1}
                alt="Elderly woman smiling"
                className="rounded-3xl w-full h-72 md:h-96 object-cover shadow-soft"
              />
              <motion.img
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15 }}
                src={GALLERY_2}
                alt="Hands holding in care"
                className="rounded-3xl w-full h-72 md:h-96 object-cover shadow-soft mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Transparency ===== */}
      <section id="transparency" className="relative py-20 md:py-32 bg-bone overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{ backgroundImage: `url(${IMPACT_IMG})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <div className="inline-block text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-4">Full transparency</div>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight text-ink font-medium">
              Where every dollar goes.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { pct: 70, label: "Care & Housing", color: "bg-terracotta" },
              { pct: 20, label: "Medical & Health", color: "bg-olive" },
              { pct: 10, label: "Operations", color: "bg-ink" },
            ].map((row, i) => (
              <div key={i} className="bg-white rounded-3xl p-8 border border-stone-border/60 shadow-soft">
                <div className="font-heading text-6xl font-semibold text-ink">{row.pct}<span className="text-2xl text-ink-muted">%</span></div>
                <div className="mt-2 text-sm uppercase tracking-wider text-ink-body font-medium">{row.label}</div>
                <div className="mt-5 h-1.5 w-full bg-bone rounded-full overflow-hidden">
                  <div className={`h-full ${row.color} rounded-full`} style={{ width: `${row.pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer className="bg-ink text-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-3 gap-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-6 h-6 text-terracotta fill-terracotta" />
              <span className="font-heading text-2xl font-semibold text-white">{config.charity_name}</span>
            </div>
            <p className="text-sm text-bone/70 leading-relaxed">
              A sanctuary for elderly who have no one to turn to. Built on compassion, sustained by community.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-4">Get in touch</div>
            <ul className="space-y-2 text-sm text-bone/70">
              <li>contact@goldenyearscare.org</li>
              <li>+1 (555) 010-2580</li>
              <li>Open 24/7 for emergencies</li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta mb-4">Payment Security</div>
            <ul className="space-y-2 text-sm text-bone/70">
              <li>Powered by NOWPayments</li>
              <li>SSL Encrypted Transactions</li>
              <li>Non-custodial crypto settlements</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 text-xs text-bone/50 flex flex-col md:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} {config.charity_name}. All rights reserved.</span>
            <span>Made with <Heart className="inline w-3 h-3 text-terracotta fill-terracotta" /> for those who gave us everything.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
